import Image from "next/image";
import Link from "next/link";
import { type BlogPost } from "@/lib/blog-types";
import type { Dictionary } from "@/i18n/dictionaries";
import type { Locale } from "@/i18n/config";

type BlogCardProps = {
  post: BlogPost;
  locale: Locale;
  dict?: Dictionary;
};

function blogHref(locale: Locale, slug: string): string {
  const prefix = locale === "en" ? "" : `/${locale}`;
  return `${prefix}/blog/${slug}`;
}

export function FeaturedBlogCard({ post, locale }: BlogCardProps) {
  return (
    <Link
      href={blogHref(locale, post.slug)}
      aria-label={post.title}
      className="group flex flex-col gap-8 lg:flex-row lg:gap-20"
    >
      <div className="flex flex-1 flex-col justify-between">
        <p className="text-sm font-medium tracking-tight text-brand-strong">
          {post.category}
        </p>
        <div className="mt-6 flex flex-col gap-3 lg:mt-0">
          <h2 className="text-[28px] font-medium leading-[1.15] tracking-[-0.04em] text-neutral-950 sm:text-[32px] sm:leading-[40px] group-hover:underline underline-offset-4 decoration-2">
            {post.title}
          </h2>
          <p className="text-base leading-6 tracking-tight text-neutral-500">
            {post.excerpt}
          </p>
          <time
            dateTime={post.isoDate}
            className="text-sm tracking-tight text-neutral-500"
          >
            {post.date}
          </time>
        </div>
      </div>
      <div
        className="relative h-[260px] flex-1 overflow-hidden rounded-xl bg-neutral-100 shadow-[var(--shadow-md)] sm:h-[350px]"
        aria-hidden
      >
        <Image
          src={post.coverImage}
          alt=""
          fill
          sizes="(min-width: 1024px) 50vw, 100vw"
          className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          fetchPriority="high"
        />
      </div>
    </Link>
  );
}

export function BlogListCard({ post, locale }: BlogCardProps) {
  return (
    <Link
      href={blogHref(locale, post.slug)}
      aria-label={post.title}
      className="group flex gap-8"
    >
      <div className="flex flex-1 flex-col justify-between py-4">
        <p className="text-sm font-medium tracking-tight text-brand-strong">
          {post.category}
        </p>
        <div className="mt-4 flex flex-col gap-3">
          <h3 className="text-[18px] font-medium leading-6 tracking-[-0.04em] text-neutral-950 group-hover:underline underline-offset-4">
            {post.title}
          </h3>
          <time
            dateTime={post.isoDate}
            className="text-sm tracking-tight text-neutral-500"
          >
            {post.date}
          </time>
        </div>
      </div>
      <div
        className="relative h-[180px] flex-1 overflow-hidden rounded-xl bg-neutral-100"
        aria-hidden
      >
        <Image
          src={post.coverImage}
          alt=""
          fill
          sizes="(min-width: 1024px) 33vw, 50vw"
          className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
      </div>
    </Link>
  );
}
